import { useTheme } from "@mui/material/styles";
// src/components/frontend/companies/HotelsMap.js
import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Marker icon fix
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [51.163375, 10.447683];
const DEFAULT_ZOOM = 6;

const resolveBackendUrl = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    const s = String(pathOrUrl).trim();
    if (!s) return null;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    const envBase = (process.env.REACT_APP_BACKEND_URL || "").trim();
    const base = (envBase || window.location.origin).replace(/\/$/, "");
    const path = s.startsWith("/") ? s : `/${s}`;
    return `${base}${path}`;
};

const normalizeCrop = (crop) => {
    const x = Number(crop?.x ?? crop?.crop_x);
    const y = Number(crop?.y ?? crop?.crop_y);
    const zoom = Number(crop?.zoom ?? crop?.crop_zoom);

    return {
        x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 50,
        y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : 50,
        zoom: Number.isFinite(zoom) ? Math.min(3, Math.max(1, zoom)) : 1,
    };
};

const escapeHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

function createMarkerIcon(palette) {
const ORANGE_BASE = palette.primary.main;
const orangeGradientSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
  <defs>
    <linearGradient id="pinGrad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${palette.primary.light}"/>
      <stop offset="55%" stop-color="${ORANGE_BASE}"/>
      <stop offset="100%" stop-color="${palette.primary.dark}"/>
    </linearGradient>
    <filter id="pinShadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="1.2" stdDeviation="1.2" flood-color="#000" flood-opacity="0.25"/>
    </filter>
  </defs>
  <path
    d="M12.5 0C5.6 0 0 5.6 0 12.5C0 22.2 12.5 41 12.5 41S25 22.2 25 12.5C25 5.6 19.4 0 12.5 0Z"
    fill="url(#pinGrad)"
    stroke="${palette.primary.dark}"
    stroke-width="0.8"
    filter="url(#pinShadow)"
  />
  <circle cx="12.5" cy="12.5" r="5.2" fill="#ffffff" opacity="0.98"/>
  <circle cx="11.3" cy="11.3" r="2.6" fill="#ffffff" opacity="0.35"/>
</svg>
`.trim();

return new L.Icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(orangeGradientSvg)}`,
    iconRetinaUrl: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(orangeGradientSvg)}`,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -36],
    shadowSize: [41, 41],
    shadowAnchor: [12, 41],
});

}

export default function HotelsMap({ markers, resizeKey }) {
    const { palette } = useTheme();
    const orangeMarkerIcon = React.useMemo(() => createMarkerIcon(palette), [palette]);
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersLayerRef = useRef(null);

    const [mapReady, setMapReady] = useState(false);

    useEffect(() => {
        if (mapRef.current) return;

        let cancelled = false;
        let retryTimer = null;
        let initialResizeTimer = null;
        let tries = 0;
        const MAX_TRIES = 80;

        const scheduleRetry = () => {
            if (cancelled) return;
            tries += 1;
            if (tries > MAX_TRIES) { setMapReady(true); return; }
            retryTimer = setTimeout(initializeMap, 120);
        };

        const initializeMap = () => {
            try {
                if (cancelled) return;
                const container = mapContainerRef.current;
                if (!container) { scheduleRetry(); return; }
                const w = container.clientWidth;
                const h = container.clientHeight;
                if (!w || !h) { scheduleRetry(); return; }
                if (mapRef.current) return;

                const map = L.map(container, {
                    // Leaflet 1.9 leaves a zoom-transition timeout alive after remove().
                    // Avoid that transition when navigating away from this routed map.
                    zoomAnimation: false,
                    zoomControl: true,
                    attributionControl: true,
                }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);

                L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                    attribution: "&copy; OpenStreetMap contributors",
                    maxZoom: 19,
                }).addTo(map);

                mapRef.current = map;
                setMapReady(true);

                initialResizeTimer = setTimeout(() => {
                    if (!cancelled && mapRef.current === map) map.invalidateSize();
                }, 0);
            } catch {
                setMapReady(true);
            }
        };

        retryTimer = setTimeout(initializeMap, 50);

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
            clearTimeout(initialResizeTimer);
            const map = mapRef.current;
            mapRef.current = null;
            if (map) { map.stop(); map.remove(); }
            markersLayerRef.current = null;
            setMapReady(false);
        };
    }, []);

    useEffect(() => {
        if (!mapRef.current || !mapReady) return;

        const map = mapRef.current;

        let disposed = false;
        const popupTimers = new Set();
        const popupFrames = new Set();
        let resizeTimer = null;
        const cleanup = () => {
            disposed = true;
            map.off("popupopen", onPopupOpen);
            popupTimers.forEach(clearTimeout);
            popupFrames.forEach(window.cancelAnimationFrame);
            clearTimeout(resizeTimer);
        };

        const adjustPopupIntoView = (popup) => {
            try {
                if (disposed || mapRef.current !== map) return;
                const popupEl = popup.getElement();
                const mapEl = mapRef.current.getContainer();
                if (!popupEl || !mapEl) return;
                const popupRect = popupEl.getBoundingClientRect();
                const mapRect = mapEl.getBoundingClientRect();
                const padding = 20;
                let dx = 0, dy = 0;
                if (popupRect.left < mapRect.left + padding) dx = popupRect.left - (mapRect.left + padding);
                else if (popupRect.right > mapRect.right - padding) dx = popupRect.right - (mapRect.right - padding);
                if (popupRect.top < mapRect.top + padding) dy = popupRect.top - (mapRect.top + padding);
                else if (popupRect.bottom > mapRect.bottom - padding) dy = popupRect.bottom - (mapRect.bottom - padding);
                if (dx === 0 && dy === 0) return;
                const center = mapRef.current.getCenter();
                const centerPx = mapRef.current.project(center);
                const newCenterPx = centerPx.add(L.point(dx, dy));
                const newCenter = mapRef.current.unproject(newCenterPx);
                mapRef.current.panTo(newCenter, { animate: true });
            } catch {}
        };

        const onPopupOpen = (e) => {
            const frame = window.requestAnimationFrame(() => {
                popupFrames.delete(frame);
                adjustPopupIntoView(e.popup);
            });
            popupFrames.add(frame);
            [120, 420].forEach((delay) => {
                const timer = setTimeout(() => {
                    popupTimers.delete(timer);
                    adjustPopupIntoView(e.popup);
                }, delay);
                popupTimers.add(timer);
            });
        };
        map.on("popupopen", onPopupOpen);

        if (markersLayerRef.current) {
            map.removeLayer(markersLayerRef.current);
            markersLayerRef.current = null;
        }

        if (!markers || markers.length === 0) {
            map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
            return cleanup;
        }

        const layer = L.layerGroup();
        const positions = [];

        markers.forEach((m) => {
            if (!Array.isArray(m.position)) return;

            const imgUrl = resolveBackendUrl(m.startImage);
            const crop = normalizeCrop(m.startImageCrop);
            const addressParts = [];
            if (m.street) addressParts.push(m.street);
            if (m.zip || m.city) addressParts.push(`${m.zip || ""} ${m.city || ""}`.trim());
            if (m.country) addressParts.push(m.country);

            const imageHtml = imgUrl ? `
                    <div class="company-popup-image">
                        <img
                            src="${imgUrl}"
                            alt="${escapeHtml(m.name || "")}"
                            style="
                                width:100%;
                                height:100%;
                                object-fit:cover;
                                object-position:${crop.x}% ${crop.y}%;
                                display:block;
                            "
                            onerror="this.style.display='none';"
                        />
                    </div>
                ` : "";

            const detailKey = m.slug || m.id;
            const detailsHtml = detailKey ? `
                    <div class="company-popup-actions">
                        <a
                            href="/companies/${escapeHtml(detailKey)}"
                            class="company-popup-button"
                            onmouseover="this.style.background='linear-gradient(135deg,var(--domain-text) 0%,var(--domain-text) 100%)';this.style.boxShadow='0 4px 20px rgba(26,37,56,0.25)';"
                            onmouseout="this.style.background='linear-gradient(135deg,var(--domain-primary-light) 0%,var(--domain-primary) 100%)';this.style.boxShadow='0 2px 12px rgba(240,172,77,0.30)';"
                        >
                            Details ansehen
                        </a>
                    </div>
                ` : "";

            const popupHtml = `
                <div class="company-popup-card">
                    ${imageHtml}
                    <div class="company-popup-body">
                        ${m.name ? `<div class="company-popup-title">${escapeHtml(m.name)}</div>` : ""}
                        ${addressParts.length ? `<div class="company-popup-address">${addressParts.map(escapeHtml).join("<br>")}</div>` : ""}
                        ${detailsHtml}
                    </div>
                </div>
            `;

            const marker = L.marker(m.position, { icon: orangeMarkerIcon });

            if (m.name || addressParts.length || imgUrl || m.id) {
                marker.bindPopup(popupHtml, {
                    maxWidth: 300,
                    minWidth: 260,
                    className: "company-popup",
                    autoPan: true,
                    keepInView: true,
                    autoPanPaddingTopLeft: [20, 20],
                    autoPanPaddingBottomRight: [20, 20],
                });
            }

            marker.addTo(layer);
            positions.push(m.position);
        });

        if (positions.length) {
            layer.addTo(map);
            markersLayerRef.current = layer;
            const bounds = L.latLngBounds(positions);
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [20, 20], maxZoom: 15 });
            }
        } else {
            map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
        }

        resizeTimer = setTimeout(() => {
            if (!disposed && mapRef.current === map) map.invalidateSize();
        }, 200);
        return cleanup;
    }, [markers, mapReady, orangeMarkerIcon]);

    useEffect(() => {
        if (!mapRef.current || !mapContainerRef.current) return undefined;

        const invalidate = () => {
            if (mapRef.current) mapRef.current.invalidateSize();
        };

        const timers = [
            setTimeout(invalidate, 0),
            setTimeout(invalidate, 180),
            setTimeout(invalidate, 360),
        ];

        let observer = null;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(invalidate);
            observer.observe(mapContainerRef.current);
        }

        return () => {
            timers.forEach(clearTimeout);
            if (observer) observer.disconnect();
        };
    }, [resizeKey, mapReady]);

    return (
        <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />

            {!mapReady && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(229,231,235,0.9)",
                    }}
                >
                    <Box sx={{ textAlign: "center" }}>
                        <CircularProgress sx={{ color: "var(--domain-primary)" }} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Karte lädt…
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
